import { useEffect, useState } from 'preact/hooks';
import { DICT, texts, useLang, type Lang } from '../i18n.js';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { ApiError } from '../api.js';
import { parseBody, postDate, sinceLastVisit, visibleLine, type FeedView, type Inline, type PostView } from '../news.js';
import { navigate } from '../router.js';
import { loadReleases, markReleases, type ReleasesView } from '../releases.js';
import { tourOverlay, tourSettled } from '../tour.js';
import { Avatar, Block, Callout, Dialog, ErrorBox, LoadError, Loading, MoreOptions, PageHead, Pill, RowMenu, toast } from '../ui.js';

/**
 * My Work › News (UX-1047). Staff read and write in English; freelancers read on their personal link in Swedish.
 * Fas 0: internal audiences only (in-house staff, freelancers & crew) – client news stays closed.
 */
const T = texts(
  {
    title: 'News',
    empty: 'No news yet',
    emptyBody: 'News from KFF shows here.',
    emptyWriter: 'Share news with in-house staff and freelancers.',
    write: 'Write a news post',
    readAll: 'Mark all as read',
    markedRead: (n: number) => `${n} post${n === 1 ? '' : 's'} marked as read`,
    all: 'All news',
    unread: 'Unread',
    pinned: 'Pinned',
    edited: 'Edited',
    by: 'By',
    project: 'Project',
    to: 'To:',
    image: 'Image',
    drafts: 'Drafts and scheduled',
    archived: 'Archived',
    posts: (n: number) => `${n} post${n === 1 ? '' : 's'}`,
    newN: (n: number) => `${n} new`,
    notFound: 'This post is not available.',
    hubTag: 'New in the Hub',
    pop: {
      title: 'Since you were last here',
      mine: 'New for you',
      show: 'Show me',
      showOn: (what: string) => `Show me · ${what}`,
      forRoles: (w: string) => `For: ${w}`,
      allChanges: "All changes in What's new",
      hub: 'New in the Hub',
      kff: 'From KFF',
      more: (n: number) => `and ${n} more in News`,
      later: 'Later',
      done: 'Got it',
      failed: "Couldn't mark the news as read – try again in News.",
      off: 'You can turn this off in My settings.'
    },
    status: {
      draft: 'Draft',
      scheduled: (d: string) => `Scheduled · ${d} 08:00`,
      archived: (d: string) => `Archived ${d}`,
      published: 'Published'
    },
    act: {
      edit: 'Edit…',
      publishAgain: 'Publish again',
      publishNow: 'Publish now',
      cancelSchedule: 'Unschedule',
      unpin: 'Unpin',
      pin: 'Pin to top',
      unpublish: 'Unpublish',
      archive: 'Archive',
      deleteDraft: 'Delete draft',
      actionsFor: (title: string) => `Actions for ${title}`,
      moreFor: (title: string) => `More for ${title}`,
      published: (title: string) => `Published: ${title}`,
      scheduleCancelled: (title: string) => `No longer scheduled – ${title} is a draft again`,
      pinnedDone: (title: string) => `Pinned to top: ${title}`,
      unpinnedDone: (title: string) => `Unpinned: ${title}`,
      unpublished: (title: string) => `Unpublished – ${title} is a draft again · readers no longer see it`,
      archivedDone: (title: string) => `Archived – ${title}`,
      draftDeleted: (title: string) => `Draft deleted – ${title}`
    },
    c: {
      editDraft: 'Edit draft',
      editPost: 'Edit post',
      write: 'Write a news post',
      sub: 'News for in-house staff and freelancers',
      title: 'Title',
      titlePh: 'What is the news?',
      text: 'Text',
      textHint: 'Simple formatting: **bold** · a line starting with "- " is a list · a blank line starts a new paragraph',
      who: 'Who sees it',
      kind: 'What is it about',
      kindKff: 'News from KFF',
      kindHub: 'New in the Hub – a new feature or a change',
      forRoles: 'For which roles',
      rolesHint: 'None ticked = every role. Admins always see it.',
      rolesAll: 'Visible to: every role',
      rolesLine: (w: string) => `Visible to: ${w} and admins`,
      everyone: 'Everyone',
      internal: 'Internal – in-house staff',
      crew: 'Freelancers & crew',
      crewHint: 'Freelance editors and camera',
      project: 'Linked project',
      optional: '(optional)',
      removeProject: 'Remove project',
      projectPh: 'Search projects',
      matching: 'Matching projects',
      link: 'Link',
      linkText: 'Link text',
      linkTextPh: 'Read more',
      image: 'Image',
      pin: 'Pin to top',
      editedNote: 'Readers see "Edited" on the post when you change the title or the text.',
      when: 'When',
      publishNow: 'Publish now',
      schedule: 'Schedule',
      publishOn: 'Publish on',
      liveAt: 'Goes live at 08:00',
      digest: 'In-house staff get the post as one line in their daily digest – no separate email. Nothing is emailed while invitations are off.',
      cancel: 'Cancel',
      saveDraft: 'Save draft',
      saveChanges: 'Save changes',
      publish: 'Publish',
      draftSaved: (title: string) => `Draft saved – ${title}`,
      scheduledFor: (d: string, title: string) => `Scheduled for ${d} 08:00 – ${title}`,
      saved: (title: string) => `Saved – ${title}`,
      published: (title: string) => `Published: ${title} · in readers' next digest`
    }
  },
  {
    title: 'Nyheter',
    empty: 'Inga nyheter än',
    emptyBody: 'Nyheter från KFF syns här.',
    emptyWriter: 'Dela nyheter med anställda och frilansare.',
    write: 'Skriv en nyhet',
    readAll: 'Markera alla som lästa',
    markedRead: (n: number) => `${n} inlägg markerade som lästa`,
    all: 'Alla nyheter',
    unread: 'Oläst',
    pinned: 'Fäst',
    edited: 'Uppdaterad',
    by: 'Av',
    project: 'Projekt',
    to: 'Till:',
    image: 'Bild',
    drafts: 'Utkast och schemalagda',
    archived: 'Arkiverade',
    posts: (n: number) => `${n} inlägg`,
    newN: (n: number) => `${n} nya`,
    notFound: 'Inlägget finns inte längre.',
    hubTag: 'Nytt i Hubben',
    pop: {
      title: 'Sedan du var här sist',
      mine: 'Nytt för dig',
      show: 'Visa mig',
      showOn: (what: string) => `Visa mig · ${what}`,
      forRoles: (w: string) => `För: ${w}`,
      allChanges: 'Alla ändringar under Vad är nytt',
      hub: 'Nytt i Hubben',
      kff: 'Från KFF',
      more: (n: number) => `och ${n} till i Nyheter`,
      later: 'Senare',
      done: 'Uppfattat',
      failed: 'Nyheterna kunde inte markeras som lästa – försök igen under Nyheter.',
      off: 'Du kan stänga av den här rutan i Mina inställningar.'
    },
    status: {
      draft: 'Utkast',
      scheduled: (d: string) => `Schemalagd · ${d} kl. 08:00`,
      archived: (d: string) => `Arkiverad ${d}`,
      published: 'Publicerad'
    },
    act: {
      edit: 'Ändra…',
      publishAgain: 'Publicera igen',
      publishNow: 'Publicera nu',
      cancelSchedule: 'Ta bort schemaläggningen',
      unpin: 'Lossa',
      pin: 'Fäst överst',
      unpublish: 'Avpublicera',
      archive: 'Arkivera',
      deleteDraft: 'Ta bort utkastet',
      actionsFor: (title: string) => `Åtgärder för ${title}`,
      moreFor: (title: string) => `Mer för ${title}`,
      published: (title: string) => `Publicerad: ${title}`,
      scheduleCancelled: (title: string) => `Inte längre schemalagd – ${title} är ett utkast igen`,
      pinnedDone: (title: string) => `Fäst överst: ${title}`,
      unpinnedDone: (title: string) => `Lossad: ${title}`,
      unpublished: (title: string) => `Avpublicerad – ${title} är ett utkast igen · läsarna ser den inte längre`,
      archivedDone: (title: string) => `Arkiverad – ${title}`,
      draftDeleted: (title: string) => `Utkastet är borttaget – ${title}`
    },
    c: {
      editDraft: 'Ändra utkast',
      editPost: 'Ändra inlägg',
      write: 'Skriv en nyhet',
      sub: 'Nyheter för anställda och frilansare',
      title: 'Rubrik',
      titlePh: 'Vad är nyheten?',
      text: 'Text',
      textHint: 'Enkel formatering: **fetstil** · en rad som börjar med "- " blir en lista · en tom rad börjar ett nytt stycke',
      who: 'Vem ser den',
      kind: 'Vad handlar den om',
      kindKff: 'Nyhet från KFF',
      kindHub: 'Nytt i Hubben – en ny funktion eller ändring',
      forRoles: 'För vilka roller',
      rolesHint: 'Ingen ikryssad = alla roller. Admin ser den alltid.',
      rolesAll: 'Syns för: alla roller',
      rolesLine: (w: string) => `Syns för: ${w} och admin`,
      everyone: 'Alla',
      internal: 'Internt – anställda',
      crew: 'Frilansare och crew',
      crewHint: 'Frilansare inom redigering och kamera',
      project: 'Kopplat projekt',
      optional: '(valfritt)',
      removeProject: 'Ta bort projektet',
      projectPh: 'Sök projekt',
      matching: 'Projekt som matchar',
      link: 'Länk',
      linkText: 'Länktext',
      linkTextPh: 'Läs mer',
      image: 'Bild',
      pin: 'Fäst överst',
      editedNote: 'Läsarna ser ”Uppdaterad” på inlägget när du ändrar rubriken eller texten.',
      when: 'När',
      publishNow: 'Publicera nu',
      schedule: 'Schemalägg',
      publishOn: 'Publicera den',
      liveAt: 'Publiceras kl. 08:00',
      digest: 'Anställda får inlägget som en rad i sitt dagliga sammandrag – inget separat mejl. Inget mejlas medan inbjudningarna är avstängda.',
      cancel: 'Avbryt',
      saveDraft: 'Spara utkast',
      saveChanges: 'Spara ändringar',
      publish: 'Publicera',
      draftSaved: (title: string) => `Utkastet är sparat – ${title}`,
      scheduledFor: (d: string, title: string) => `Schemalagd till ${d} kl. 08:00 – ${title}`,
      saved: (title: string) => `Sparat – ${title}`,
      published: (title: string) => `Publicerad: ${title} · i läsarnas nästa sammandrag`
    }
  }
);

const inl = (parts: Inline[]) => parts.map((x, i) => (x.bold ? <strong key={i}>{x.text}</strong> : x.text));

/** The post text as elements (never HTML). */
export function PostBody(p: { md: string }) {
  return (
    <div class="news-body">
      {parseBody(p.md).map((b, i) =>
        b.kind === 'p' ? (
          <p key={i}>
            {b.lines.map((l, j) => (
              <>
                {j ? <br /> : null}
                {inl(l)}
              </>
            ))}
          </p>
        ) : (
          <ul key={i}>
            {b.items.map((it, j) => (
              <li key={j}>{inl(it)}</li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

function Meta(p: { post: PostView; lang: Lang }) {
  const t = T[p.lang];
  const x = p.post;
  return (
    <p class="news-meta small">
      {x.pinned ? <span class="news-pin">{t.pinned}</span> : null}
      {x.category === 'hub' ? <span class="news-tag">{t.hubTag}</span> : null}
      <span class="news-author">
        <Avatar name={x.author.name} size="sm" decorative />
        {x.author.name}
      </span>
      {x.publishedAt && x.status === 'published' ? <span>{postDate(x.publishedAt, p.lang)}</span> : null}
      {x.editedAt ? (
        <span class="news-edited" title={`${t.edited} ${postDate(x.editedAt, p.lang, new Date(0))}`}>
          {t.edited}
        </span>
      ) : null}
      {x.audience ? <span class="news-aud">{`${t.to} ${x.audience.chips}`}</span> : null}
    </p>
  );
}

/** A post's state for its author: Draft · Scheduled · Archived · Published. */
export const statusText = (x: Pick<PostView, 'status' | 'publishAt' | 'archivedAt'>, lang: Lang = 'en') => {
  const t = T[lang].status;
  return x.status === 'draft' ? t.draft : x.status === 'scheduled' && x.publishAt ? t.scheduled(postDate(x.publishAt, lang)) : x.status === 'archived' && x.archivedAt ? t.archived(postDate(x.archivedAt, lang, new Date(0))) : t.published;
};

/** Post menu (prototype postMenu) – every action has Undo. */
function PostActions(p: { api: Api; post: PostView; lang: Lang; reload: () => Promise<void>; edit?: (id: string) => void; onGone?: () => void }) {
  const t = T[p.lang].act;
  const x = p.post;
  if (!x.canManage) return null;
  const run = async (label: string, call: () => Promise<{ undo: unknown }>, gone = false) => {
    try {
      const r = await call();
      await p.reload();
      if (gone) p.onGone?.();
      toast({
        text: label,
        undo: async () => {
          await p.api.post('/api/news/restore', { post: r.undo });
          await p.reload();
        }
      });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  const act = (a: string) => () => p.api.post<{ undo: unknown }>(`/api/news/${x.id}/${a}`);
  const items: { label: string; run: () => void; danger?: boolean }[] = [];
  if (x.status !== 'archived' && p.edit) items.push({ label: t.edit, run: () => p.edit!(x.id) });
  if (x.status === 'draft' || x.status === 'scheduled' || x.status === 'archived') items.push({ label: x.status === 'archived' ? t.publishAgain : t.publishNow, run: () => void run(t.published(x.title), act('publish')) });
  if (x.status === 'scheduled') items.push({ label: t.cancelSchedule, run: () => void run(t.scheduleCancelled(x.title), act('unpublish')) });
  if (x.status === 'published') {
    items.push({ label: x.pinned ? t.unpin : t.pin, run: () => void run(x.pinned ? t.unpinnedDone(x.title) : t.pinnedDone(x.title), act(x.pinned ? 'unpin' : 'pin')) });
    items.push({ label: t.unpublish, run: () => void run(t.unpublished(x.title), act('unpublish'), true) });
    items.push({ label: t.archive, run: () => void run(t.archivedDone(x.title), act('archive'), true) });
  }
  if (x.status === 'draft') items.push({ label: t.deleteDraft, run: () => void run(t.draftDeleted(x.title), () => p.api.del<{ undo: unknown }>(`/api/news/${x.id}`), true), danger: true });
  // the next step stays a button (Edit, and Publish for a draft); the rest sit under ⋯ so a post has two buttons, not five
  const shown = items.slice(0, x.status === 'published' ? 1 : 2);
  return (
    <div class="cluster news-actions" role="group" aria-label={t.actionsFor(x.title)}>
      {shown.map((a) => (
        <button type="button" class="btn btn-sm" onClick={a.run} aria-label={`${a.label}: ${x.title}`}>
          {a.label}
        </button>
      ))}
      <RowMenu label={t.moreFor(x.title)} items={items.slice(shown.length)} />
    </div>
  );
}

function Card(p: { post: PostView; lang: Lang; open: (id: string) => void; level: 2 | 3; children?: preact.ComponentChildren }) {
  const t = T[p.lang];
  const x = p.post;
  const H = p.level === 2 ? 'h2' : 'h3';
  return (
    <article class={`news-card${x.unread ? ' is-unread' : ''}${x.pinned ? ' is-pinned' : ''}`} aria-labelledby={`np-${x.id}`}>
      <H class="news-title" id={`np-${x.id}`}>
        {x.unread ? (
          <span class="news-dot" title={t.unread}>
            <span class="sr-only">{t.unread}: </span>
          </span>
        ) : null}
        <a href={`/news/${x.id}`} onClick={(e) => (e.preventDefault(), p.open(x.id))}>
          {x.title}
        </a>
      </H>
      <Meta post={x} lang={p.lang} />
      {x.project ? <p class="news-proj small">{`${t.project}: ${x.project.name}`}</p> : null}
      {x.excerpt ? <p class="news-excerpt">{x.excerpt}</p> : null}
      {x.seen ? <p class="small muted" title={x.seen.names.slice(0, 12).join(', ')}>{x.seen.text}</p> : null}
      {p.children}
    </article>
  );
}

export function PostFull(p: { api: Api; id: string; lang: Lang; back: () => void; edit?: (id: string) => void; reloadFeed?: () => void }) {
  const t = T[p.lang];
  const [post, setPost] = useState<PostView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const load = async () => {
    try {
      setPost((await p.api.get<{ post: PostView }>(`/api/news/${p.id}`)).post);
      p.reloadFeed?.();
    } catch (e) {
      setErr(e instanceof ApiError && e.status === 404 ? new Error(t.notFound) : e);
    }
  };
  useEffect(() => {
    void load();
  }, [p.id]);
  useEffect(() => {
    if (post) document.getElementById('news-full-h')?.focus();
  }, [post?.id]);
  const back = (
    <p>
      <a class="back-link" href={p.edit ? '/news' : '#'} onClick={(e) => (e.preventDefault(), p.back())}>
        ← {t.all}
      </a>
    </p>
  );
  if (err)
    return (
      <div class="stack">
        {back}
        <ErrorBox error={err} />
      </div>
    );
  if (!post) return <Loading label={p.lang === 'sv' ? DICT.sv.common.loading : undefined} />;
  return (
    <div class="stack">
      {back}
      <article class="news-full blk" aria-labelledby="news-full-h">
        <h1 class="news-full-title" id="news-full-h" tabIndex={-1}>
          {post.title}
        </h1>
        <Meta post={post} lang={p.lang} />
        {post.status !== 'published' ? <Pill tone="neutral">{statusText(post, p.lang)}</Pill> : null}
        {post.image ? (
          <figure class="news-img" aria-label={t.image}>
            <span aria-hidden="true">▧</span>
          </figure>
        ) : null}
        <PostBody md={post.body} />
        {post.link ? (
          <p>
            <a href={post.link.url} target="_blank" rel="noopener noreferrer">
              {post.link.label} ↗
            </a>
          </p>
        ) : null}
        {post.project ? <p class="news-proj small">{`${t.project}: ${post.project.name}`}</p> : null}
        {post.seen ? <p class="small muted">{post.seen.text}{post.seen.names.length ? ` – ${post.seen.names.slice(0, 12).join(', ')}${post.seen.names.length > 12 ? ' …' : ''}` : ''}</p> : null}
        <PostActions api={p.api} post={post} lang={p.lang} reload={load} edit={p.edit} onGone={() => (post.status === 'draft' ? p.back() : undefined)} />
      </article>
    </div>
  );
}

/** The feed with unread markers, *Mark all as read* and – for writers – drafts, scheduled and archived posts. */
export function NewsFeed(p: { api: Api; lang: Lang; postId?: string | null; open: (id: string | null) => void; edit?: (id: string) => void; write?: () => void; heading?: boolean }) {
  const t = T[p.lang];
  const [v, setV] = useState<FeedView | null>(() => peek(p.api, '/api/news') ?? null);
  const [err, setErr] = useState<unknown>(null);
  const load = () => p.api.get<FeedView>('/api/news').then(setV, setErr);
  useEffect(() => {
    void load();
  }, []);
  if (p.postId) return <PostFull api={p.api} id={p.postId} lang={p.lang} back={() => p.open(null)} edit={p.edit} reloadFeed={() => void load()} />;
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading label={p.lang === 'sv' ? DICT.sv.common.loading : undefined} />;
  const readAll = async () => {
    const r = await p.api.post<{ marked: number }>('/api/news/read-all');
    await load();
    toast({ text: t.markedRead(r.marked) });
  };
  const sub = v.feed.length ? t.posts(v.feed.length) + (v.unread ? ` · ${t.newN(v.unread)}` : '') : t.empty;
  return (
    <div class="stack news">
      {p.heading === false ? (
        <h2 class="blk-title">{t.title}</h2>
      ) : (
        <PageHead
          title={t.title}
          sub={sub}
          action={
            v.canPost && p.write ? (
              <button type="button" class="btn btn-primary" onClick={p.write}>
                + {t.write}
              </button>
            ) : null
          }
        />
      )}
      {v.unread > 1 ? (
        <p>
          <button type="button" class="btn btn-ghost btn-sm" onClick={() => void readAll()}>
            {t.readAll}
          </button>
        </p>
      ) : null}
      {v.feed.length ? (
        <div class="news-feed">
          {v.feed.map((x) => (
            <Card key={x.id} post={x} lang={p.lang} level={p.heading === false ? 3 : 2} open={(id) => p.open(id)}>
              <PostActions api={p.api} post={x} lang={p.lang} reload={load} edit={p.edit} />
            </Card>
          ))}
        </div>
      ) : (
        <div class="empty">
          <strong>{t.empty}</strong>
          <span class="muted">{v.canPost ? t.emptyWriter : t.emptyBody}</span>
        </div>
      )}
      {v.drafts.length ? (
        <Block title={`${t.drafts} (${v.drafts.length})`} id="news-drafts">
          <ul class="list">
            {v.drafts.map((x) => (
              <li class="list-row" key={x.id}>
                <span>
                  <a href={`/news/${x.id}`} onClick={(e) => (e.preventDefault(), p.open(x.id))}>
                    {x.title}
                  </a>
                  <span class="cell-sub">{`${statusText(x, p.lang)} · ${x.author.name}${x.audience ? ` · ${t.to} ${x.audience.chips}` : ''}`}</span>
                </span>
                <PostActions api={p.api} post={x} lang={p.lang} reload={load} edit={p.edit} />
              </li>
            ))}
          </ul>
        </Block>
      ) : null}
      {v.archived.length ? (
        <details class="blk">
          <summary class="blk-title">{`${t.archived} (${v.archived.length})`}</summary>
          <ul class="list">
            {v.archived.map((x) => (
              <li class="list-row" key={x.id}>
                <span>
                  {x.title}
                  <span class="cell-sub">{`${statusText(x, p.lang)} · ${x.author.name}`}</span>
                </span>
                <PostActions api={p.api} post={x} lang={p.lang} reload={load} />
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

/** My Work › Now (UX-1047): ONE quiet line while something is unread, and the latest posts with *All news*. */
export function NewsOnNow(p: { api: Api; open: (id: string | null) => void }) {
  const lang = useLang();
  const t = DICT[lang].newsNow;
  const [v, setV] = useState<FeedView | null>(null);
  useEffect(() => {
    const load = () => void p.api.get<FeedView>('/api/news').then(setV, () => setV(null));
    load();
    // the start-up popup marked posts as read
    addEventListener(NEWS_READ, load);
    return () => removeEventListener(NEWS_READ, load);
  }, []);
  if (!v) return null;
  const unread = v.feed.filter((x) => x.unread);
  const first = unread[0];
  return (
    <section class="blk" aria-labelledby="now-news">
      <div class="blk-head">
        <h2 class="blk-title" id="now-news">
          {t.title}
        </h2>
        <a href="/news" onClick={(e) => (e.preventDefault(), p.open(null))}>
          {t.all}{v.feed.length ? ` (${v.feed.length})` : ''}
        </a>
      </div>
      {first ? (
        <p class="small news-nowline">
          {unread.length === 1 ? t.newOne(first.title) : t.newMany(unread.length, first.title)} ·{' '}
          <a href={`/news/${first.id}`} onClick={(e) => (e.preventDefault(), p.open(unread.length === 1 ? first.id : null))}>
            {t.read}
          </a>
        </p>
      ) : null}
      {v.feed.length ? (
        <ul class="list">
          {v.feed.slice(0, 3).map((x) => (
            <li key={x.id} class="list-row">
              <a href={`/news/${x.id}`} onClick={(e) => (e.preventDefault(), p.open(x.id))}>
                {x.unread ? <span class="sr-only">{t.unread}</span> : null}
                {x.title}
              </a>
              <span class="small muted">{x.publishedAt ? postDate(x.publishedAt, lang) : ''}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p class="small muted">{t.none}</p>
      )}
    </section>
  );
}

// ---- the start-up popup ("Since you were last here") -----------------------------------------------------------------

/** Fired when the popup marks posts as read, so the Now page's news block refreshes. */
export const NEWS_READ = 'kff:news-read';
const POPUP_SEEN = 'kff.news.popup';
const popupSeen = (): boolean => {
  try {
    return sessionStorage.getItem(POPUP_SEEN) === '1';
  } catch {
    return false;
  }
};
const markPopupSeen = () => {
  try {
    sessionStorage.setItem(POPUP_SEEN, '1');
  } catch {
    /* private mode: the popup may show again on the next start */
  }
};

/**
 * When the Hub opens: what happened since you were last here – the posts you haven't read, "New in the Hub" first,
 * then "From KFF". Once per browser session; *Got it* marks them read, *Later* (or Escape) leaves them unread for the
 * next start. Nothing shows when everything is read.
 */
export function WhatsNew(p: { api: Api; open: (id: string) => void; go?: (path: string) => void }) {
  const lang = useLang();
  const t = T[lang];
  const [v, setV] = useState<ReturnType<typeof sinceLastVisit> | null>(null);
  const [rel, setRel] = useState<ReleasesView | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (popupSeen()) return;
    let live = true;
    // "New for you" (my roles' release entries) first, then the unread posts; nothing shows when both are empty.
    // One thing over the page at a time: the page's guided tour goes first, and someone who had never seen a tour gets
    // this popup on their next visit instead (tour.tsx).
    void Promise.all([p.api.get<FeedView>('/api/news').then((f) => sinceLastVisit(f.feed), () => null), loadReleases(p.api), tourSettled()]).then(([s, r, tour]) => {
      if (!live || tour.skipNews) return;
      if (!s?.ids.length && !r.items.length) return;
      tourOverlay(true);
      setV(s ?? { hub: [], kff: [], more: 0, ids: [] });
      setRel(r);
    });
    return () => {
      live = false;
      tourOverlay(false);
    };
  }, []);
  if (!v) return null;
  const close = () => {
    markPopupSeen();
    tourOverlay(false);
    setV(null);
  };
  const shown = rel?.items.map((x) => x.key) ?? [];
  const done = async () => {
    setBusy(true);
    try {
      await Promise.all([v.ids.length ? p.api.post('/api/news/read-all', { ids: v.ids }) : null, markReleases(p.api, shown)]);
      dispatchEvent(new Event(NEWS_READ));
    } catch {
      toast({ text: t.pop.failed });
    }
    close();
  };
  const mine = rel?.items.length ? (
    <section class="news-pop-group" aria-label={t.pop.mine}>
      <h3 class="news-pop-h">{t.pop.mine}</h3>
      <ul class="news-pop-list">
        {rel.items.map((x) => (
          <li key={x.key} class="news-pop-rel">
            <span class="news-pop-title">{x.title}</span>
            {x.forRoles ? <span class="news-pop-for">{t.pop.forRoles(x.forRoles)}</span> : null}
            <p class="news-pop-excerpt small">{x.text}</p>
            {x.show ? (
              <a
                href={x.show.path}
                class="btn btn-sm news-pop-show"
                onClick={(e) => {
                  e.preventDefault();
                  // "Show me" counts as seen: it is in front of them now
                  void markReleases(p.api, [x.key]).catch(() => undefined);
                  close();
                  (p.go ?? navigate)(x.show!.path);
                }}
              >
                {x.show.label ? t.pop.showOn(x.show.label) : t.pop.show}
              </a>
            ) : null}
          </li>
        ))}
      </ul>
      <a
        href="/settings/whats-new"
        class="small"
        onClick={(e) => {
          e.preventDefault();
          close();
          (p.go ?? navigate)('/settings/whats-new');
        }}
      >
        {t.pop.allChanges}
      </a>
    </section>
  ) : null;
  const group = (title: string, posts: PostView[]) =>
    posts.length ? (
      <section class="news-pop-group" aria-label={title}>
        <h3 class="news-pop-h">{title}</h3>
        <ul class="news-pop-list">
          {posts.map((x) => (
            <li key={x.id}>
              <a
                href={`/news/${x.id}`}
                class="news-pop-title"
                onClick={(e) => {
                  e.preventDefault();
                  close();
                  p.open(x.id);
                }}
              >
                {x.title}
              </a>
              <span class="small muted">{`${x.publishedAt ? postDate(x.publishedAt, lang) : ''} · ${x.author.name}`}</span>
              {x.excerpt ? <p class="news-pop-excerpt small">{x.excerpt}</p> : null}
            </li>
          ))}
        </ul>
      </section>
    ) : null;
  return (
    <Dialog
      title={t.pop.title}
      onClose={close}
      foot={
        <>
          <button type="button" class="btn" onClick={close}>
            {t.pop.later}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void done()}>
            {t.pop.done}
          </button>
        </>
      }
    >
      {mine}
      {group(t.pop.hub, v.hub)}
      {group(t.pop.kff, v.kff)}
      {v.more ? (
        <p class="small">
          <a
            href="/news"
            onClick={(e) => {
              e.preventDefault();
              close();
              p.open('');
            }}
          >
            {t.pop.more(v.more)}
          </a>
        </p>
      ) : null}
      <p class="small muted">{t.pop.off}</p>
    </Dialog>
  );
}

// ---- the composer ("Write a news post" / "Edit post") ---------------------------------------------------------------

interface ComposerState {
  title: string;
  body: string;
  internal: boolean;
  crew: boolean;
  project: { id: string; name: string } | null;
  linkUrl: string;
  linkLabel: string;
  category: 'kff' | 'hub';
  /** "New in the Hub": the roles it is for; empty = every role */
  roles: string[];
  pinned: boolean;
  when: 'now' | 'later';
  date: string;
}

type Errors = Partial<Record<'title' | 'body' | 'audience' | 'linkUrl' | 'publishOn' | 'project' | 'form', string>>;

function FieldError(p: { id: string; msg?: string }) {
  return p.msg ? (
    <span class="field-error" id={p.id}>
      {p.msg}
    </span>
  ) : null;
}

/** "New in the Hub" goes by role, never employment form (owner 2026-10-31). */
const ROLES: { key: string; label: Record<Lang, string> }[] = [
  { key: 'producer', label: { en: 'Producer', sv: 'Producent' } },
  { key: 'editor', label: { en: 'Editing', sv: 'Redigering' } },
  { key: 'camera', label: { en: 'Camera', sv: 'Kamera' } },
  { key: 'admin', label: { en: 'Admin', sv: 'Admin' } }
];

export function NewsComposer(p: { api: Api; postId?: string; done: (id?: string) => void }) {
  const lang = useLang();
  const t = T[lang].c;
  const [orig, setOrig] = useState<PostView | null>(null);
  const [st, setSt] = useState<ComposerState | null>(p.postId ? null : { title: '', body: '', internal: true, crew: false, project: null, linkUrl: '', linkLabel: '', category: 'kff', roles: [], pinned: false, when: 'now', date: '' });
  const [tomorrow, setTomorrow] = useState('');
  const [errs, setErrs] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ id: string; name: string; group: string | null }[]>([]);
  const [fatal, setFatal] = useState<unknown>(null);
  useEffect(() => {
    p.api.get<FeedView>('/api/news').then((f) => setTomorrow(f.tomorrow), () => undefined);
    if (p.postId)
      p.api.get<{ post: PostView }>(`/api/news/${p.postId}`).then(({ post }) => {
        setOrig(post);
        setSt({
          title: post.title,
          body: post.body,
          internal: !!post.audience?.internal,
          crew: !!post.audience?.crew,
          project: post.project,
          linkUrl: post.link?.url ?? '',
          linkLabel: post.link && post.link.label !== post.link.url.replace(/^https?:\/\//, '').replace(/\/$/, '') ? post.link.label : '',
          category: post.category === 'hub' ? 'hub' : 'kff',
          roles: post.roles?.keys ?? [],
          pinned: post.pinned,
          when: post.status === 'scheduled' ? 'later' : 'now',
          date: post.publishAt ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date(post.publishAt)) : ''
        });
      }, setFatal);
  }, [p.postId]);
  useEffect(() => {
    if (!q.trim()) return setResults([]);
    const id = setTimeout(() => {
      p.api.get<{ projects: { id: string; name: string; group: string | null }[] }>(`/api/news/projects?q=${encodeURIComponent(q.trim())}`).then((r) => setResults(r.projects), () => setResults([]));
    }, 150);
    return () => clearTimeout(id);
  }, [q]);
  if (fatal) return <LoadError error={fatal} />;
  if (!st) return <Loading label={DICT[lang].common.loading} />;
  const live = orig?.status === 'published';
  const set = (patch: Partial<ComposerState>) => setSt({ ...st, ...patch });
  const primary = live ? t.saveChanges : st.when === 'later' ? t.schedule : t.publish;
  const save = async (mode: 'draft' | 'final') => {
    setErrs({});
    const body = {
      title: st.title,
      body: st.body,
      audience: { internal: st.internal, crew: st.crew },
      mondayItemId: st.project?.id ?? null,
      linkUrl: st.linkUrl.trim() || null,
      linkLabel: st.linkLabel.trim() || null,
      category: st.category,
      roles: st.category === 'hub' ? st.roles : null,
      pinned: st.pinned,
      ...(live ? {} : { mode: mode === 'draft' ? 'draft' : st.when === 'later' ? 'schedule' : 'publish', publishOn: st.when === 'later' ? st.date : undefined })
    };
    setBusy(true);
    try {
      const r = orig ? await p.api.put<{ post: PostView & { status: string; publishAt: string | null }; undo: unknown }>(`/api/news/${orig.id}`, body) : await p.api.post<{ post: { id: string; title: string; status: string; publishAt: string | null } }>('/api/news', body);
      const post = r.post;
      const msg =
        post.status === 'draft' ? t.draftSaved(post.title) : post.status === 'scheduled' && post.publishAt ? t.scheduledFor(postDate(post.publishAt, lang), post.title) : live ? t.saved(post.title) : t.published(post.title);
      const undo = 'undo' in r && r.undo ? r.undo : null;
      toast({
        text: msg,
        undo: async () => {
          if (undo) await p.api.post('/api/news/restore', { post: undo });
          else await p.api.del(`/api/news/${post.id}`).catch(async () => {
            await p.api.post(`/api/news/${post.id}/unpublish`);
            await p.api.del(`/api/news/${post.id}`);
          });
        }
      });
      p.done(post.status === 'published' ? post.id : undefined);
    } catch (e) {
      const d = e instanceof ApiError ? (e.details as { errors?: Errors } | undefined) : undefined;
      const fe: Errors = d?.errors ?? { form: e instanceof Error ? e.message : String(e) };
      setErrs(fe);
      const firstKey = (['title', 'audience', 'project', 'linkUrl', 'publishOn'] as const).find((k) => fe[k]);
      // after the render that opens More options, so a field inside it can take focus
      setTimeout(() => document.getElementById(firstKey === 'audience' ? 'nw-int' : firstKey === 'publishOn' ? 'nw-date' : firstKey === 'project' ? 'nw-projq' : firstKey ? `nw-${firstKey}` : 'nw-title')?.focus());
    } finally {
      setBusy(false);
    }
  };
  const inv = (k: keyof Errors) => (errs[k] ? { 'aria-invalid': true as const, 'aria-describedby': `nw-${k}-err` } : {});
  // Defaults are all in-house staff, published now: anything else is an extra under More options.
  const extras = [st.category === 'hub', !st.internal || st.crew, !!st.project, !!st.linkUrl.trim(), st.pinned, !live && st.when === 'later'].filter(Boolean).length;
  const moreErr = !!(errs.audience || errs.project || errs.linkUrl || errs.publishOn);
  return (
    <div class="screen news-page">
      <PageHead title={orig ? (orig.status === 'draft' ? t.editDraft : t.editPost) : t.write} sub={t.sub} />
      <form
        class="stack blk"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void save('final');
        }}
      >
        <div class="field">
          <label class="label" for="nw-title">
            {t.title}
          </label>
          <input class="input input--wide" id="nw-title" maxLength={120} required autoComplete="off" placeholder={t.titlePh} value={st.title} onInput={(e) => set({ title: (e.target as HTMLInputElement).value })} {...inv('title')} />
          <FieldError id="nw-title-err" msg={errs.title} />
        </div>
        <div class="field">
          <label class="label" for="nw-body">
            {t.text}
          </label>
          <textarea class="textarea" id="nw-body" rows={8} value={st.body} onInput={(e) => set({ body: (e.target as HTMLTextAreaElement).value })} aria-describedby="nw-body-hint" />
          <span class="hint" id="nw-body-hint">
            {t.textHint}
          </span>
        </div>
        <p class="small" aria-live="polite">
          {st.category === 'hub' ? (st.roles.length ? t.rolesLine(ROLES.filter((r) => st.roles.includes(r.key)).map((r) => r.label[lang]).join(', ')) : t.rolesAll) : visibleLine(st.internal, st.crew, lang)}
        </p>
        <MoreOptions id="news-compose" filled={extras} open={moreErr}>
        <fieldset class="field fieldset">
          <legend class="label">{t.kind}</legend>
          <label class="check">
            <input type="radio" name="nw-kind" checked={st.category === 'kff'} onChange={() => set({ category: 'kff' })} /> {t.kindKff}
          </label>
          <label class="check">
            <input type="radio" name="nw-kind" checked={st.category === 'hub'} onChange={() => set({ category: 'hub' })} /> {t.kindHub}
          </label>
        </fieldset>
        {st.category === 'hub' ? (
          <fieldset class="field fieldset">
            <legend class="label">{t.forRoles}</legend>
            {ROLES.map((r) => (
              <label class="check" key={r.key}>
                <input type="checkbox" checked={st.roles.includes(r.key)} onChange={(e) => set({ roles: (e.target as HTMLInputElement).checked ? [...st.roles, r.key] : st.roles.filter((x) => x !== r.key) })} /> {r.label[lang]}
              </label>
            ))}
            <span class="hint">{t.rolesHint}</span>
          </fieldset>
        ) : (
        <fieldset class="field fieldset">
          <legend class="label">{t.who}</legend>
          <label class="check">
            <input type="checkbox" checked={st.internal && st.crew} onChange={(e) => set({ internal: (e.target as HTMLInputElement).checked, crew: (e.target as HTMLInputElement).checked })} /> {t.everyone}
          </label>
          <label class="check">
            <input type="checkbox" id="nw-int" checked={st.internal} onChange={(e) => set({ internal: (e.target as HTMLInputElement).checked })} {...inv('audience')} /> {t.internal}
          </label>
          <label class="check">
            <input type="checkbox" checked={st.crew} onChange={(e) => set({ crew: (e.target as HTMLInputElement).checked })} /> {t.crew} <span class="hint">{t.crewHint}</span>
          </label>
          <FieldError id="nw-audience-err" msg={errs.audience} />
        </fieldset>
        )}
        <div class="field">
          <label class="label" for="nw-projq">
            {t.project} <span class="muted">{t.optional}</span>
          </label>
          {st.project ? (
            <p class="cluster">
              <span>{st.project.name}</span>
              <button type="button" class="btn btn-sm" onClick={() => set({ project: null })}>
                {t.removeProject}
              </button>
            </p>
          ) : (
            <>
              <input class="input input--wide" type="search" id="nw-projq" autoComplete="off" placeholder={t.projectPh} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} {...inv('project')} />
              {results.length ? (
                <ul class="list news-picks" aria-label={t.matching}>
                  {results.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        class="btn btn-sm"
                        onClick={() => {
                          set({ project: { id: r.id, name: r.name } });
                          setQ('');
                        }}
                      >
                        {r.name}
                        {r.group ? <span class="muted"> · {r.group}</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
          <FieldError id="nw-project-err" msg={errs.project} />
        </div>
        <div class="form-row">
          <div class="field">
            <label class="label" for="nw-linkUrl">
              {t.link} <span class="muted">{t.optional}</span>
            </label>
            <input class="input input--wide" id="nw-linkUrl" type="url" inputMode="url" placeholder="https://" value={st.linkUrl} onInput={(e) => set({ linkUrl: (e.target as HTMLInputElement).value })} {...inv('linkUrl')} />
            <FieldError id="nw-linkUrl-err" msg={errs.linkUrl} />
          </div>
          <div class="field">
            <label class="label" for="nw-linkLabel">
              {t.linkText} <span class="muted">{t.optional}</span>
            </label>
            <input class="input input--wide" id="nw-linkLabel" placeholder={t.linkTextPh} value={st.linkLabel} onInput={(e) => set({ linkLabel: (e.target as HTMLInputElement).value })} />
          </div>
        </div>
        <label class="check">
          <input type="checkbox" checked={st.pinned} onChange={(e) => set({ pinned: (e.target as HTMLInputElement).checked })} /> {t.pin}
        </label>
        {live ? (
          <p class="small muted">{t.editedNote}</p>
        ) : (
          <fieldset class="field fieldset">
            <legend class="label">{t.when}</legend>
            <label class="check">
              <input type="radio" name="nw-when" checked={st.when === 'now'} onChange={() => set({ when: 'now' })} /> {t.publishNow}
            </label>
            <label class="check">
              <input type="radio" name="nw-when" checked={st.when === 'later'} onChange={() => set({ when: 'later', date: st.date || tomorrow })} /> {t.schedule}
            </label>
            {st.when === 'later' ? (
              <span class="cluster">
                <label for="nw-date">{t.publishOn}</label>
                <input class="input input--date" type="date" id="nw-date" min={tomorrow} value={st.date} onInput={(e) => set({ date: (e.target as HTMLInputElement).value })} aria-describedby="nw-date-hint" {...inv('publishOn')} />
                <span class="hint" id="nw-date-hint">
                  {t.liveAt}
                </span>
              </span>
            ) : null}
            <FieldError id="nw-publishOn-err" msg={errs.publishOn} />
          </fieldset>
        )}
        </MoreOptions>
        <p class="small muted">{t.digest}</p>
        {errs.form ? <Callout tone="warn" title={errs.form} /> : null}
        <div class="cluster">
          <button type="button" class="btn" onClick={() => p.done(orig?.id)}>
            {t.cancel}
          </button>
          {live ? null : (
            <button type="button" class="btn" disabled={busy} onClick={() => void save('draft')}>
              {t.saveDraft}
            </button>
          )}
          <button type="submit" class="btn btn-primary" disabled={busy}>
            {primary}
          </button>
        </div>
      </form>
    </div>
  );
}
